import JSDB from '../fromKotlin/nk';
import {refreshDocsCall, sendReqForApi} from '../api/customer/documents.api';
import {documentTypes} from '../types/DocumentsTypes/utils';

export const hasExternalSoftwareIntegration = (): boolean => {
    const apiConfig = JSDB().apiConfig;
    return !!(apiConfig && typeof apiConfig.hasExternalSoftware === 'function' && apiConfig.hasExternalSoftware());
};

export const isDocSentToExternalSoftware = (doc: any): boolean => {
    if (!doc) return false;
    if (doc.sentToExternalSoftware || doc.externalSoftware?.send) return true;
    try {
        if (doc.nkObject?.isSentToExternalSoftware?.()) return true;
    } catch {
        /* live Kotlin object may not expose the helper until the JS bundle is rebuilt */
    }
    return !!(doc.storeNext?.send || doc.one?.send || doc.manager?.send || doc.hashavshevet?.send);
};

export const resolveExternalSoftwareDocType = (doc: any): number | null => {
    try {
        const t = doc?.nkObject?.getConnectedDocType?.();
        if (typeof t === 'number' && t >= 0) return t;
    } catch {
        /* mangled / missing helper */
    }
    const fromType = doc?.type != null ? documentTypes[doc.type]?.nkType : undefined;
    if (typeof fromType === 'number') return fromType;
    return null;
};

export const collectRowExternalSoftwareDocs = (row: any): any[] => {
    const dvs = (row?.dvs ?? []).filter((d: any) => d && d.id && d.status !== 'canceled');
    if (dvs.length > 0) return dvs;
    if (row?.nkObject && row?.orderId) {
        return [{
            id: Number(row.orderId),
            nkObject: row.nkObject,
            type: 'order',
            sentToExternalSoftware: row.sentToExternalSoftware,
            one: row.one,
            manager: row.manager,
            storeNext: row.storeNext,
            hashavshevet: row.hashavshevet,
            externalSoftware: row.externalSoftware
        }];
    }
    return [];
};

export const getRowExternalSoftwareSentStatus = (row: any): string => {
    const docs = collectRowExternalSoftwareDocs(row);
    if (!docs.length) return '';
    return docs.every(isDocSentToExternalSoftware) ? 'כן נשלח' : 'לא נשלח';
};

export const getExternalSoftwareSendTargets = (docs: any[]) => (
    docs
        .map((d) => ({id: Number(d?.id), type: resolveExternalSoftwareDocType(d)}))
        .filter((d) => d.id && d.type != null && d.type >= 0)
);

/** Same SEND_API_REQ path as DeliveryCertificatePreview (`sendReqForApi(id, type)`). */
export const sendDocsToExternalSoftware = async (docs: any[]) => {
    const valid = getExternalSoftwareSendTargets(docs);
    if (!valid.length) {
        throw new Error('no documents to send');
    }
    for (const d of valid) {
        await sendReqForApi(d.id, d.type as number);
    }
    return valid;
};

export const sendAndRefreshDocsToExternalSoftware = async (docs: any[]) => {
    const valid = await sendDocsToExternalSoftware(docs);
    return refreshDocsCall(
        valid.map((d) => d.id),
        valid.map((d) => d.type as number),
        false,
        true
    );
};

export const mergeRefreshedDocsIntoRow = (row: any, refreshed: any[]) => {
    if (!row) return row;
    const byId = new Map((refreshed || []).map((d) => [Number(d.id), d]));
    const dvs = Array.isArray(row.dvs) && row.dvs.length
        ? row.dvs.map((d: any) => byId.get(Number(d.id)) ?? d)
        : row.dvs;
    const orderDoc = row.orderId != null ? byId.get(Number(row.orderId)) : undefined;
    return {
        ...row,
        dvs,
        nkObject: orderDoc?.nkObject ?? row.nkObject,
        sentToExternalSoftware: orderDoc?.sentToExternalSoftware ?? row.sentToExternalSoftware,
        one: orderDoc?.one ?? row.one,
        manager: orderDoc?.manager ?? row.manager,
        storeNext: orderDoc?.storeNext ?? row.storeNext,
        hashavshevet: orderDoc?.hashavshevet ?? row.hashavshevet,
        externalSoftware: orderDoc?.externalSoftware ?? row.externalSoftware
    };
};

export const markDocsSentToExternalSoftware = (docs: any[]) => {
    docs.forEach((d) => {
        d.sentToExternalSoftware = true;
        d.externalSoftware = {...(d.externalSoftware || {}), send: 1};
        if (d.storeNext) d.storeNext = {...d.storeNext, send: 1};
        if (d.one) d.one = {...d.one, send: 1};
        if (d.manager) d.manager = {...d.manager, send: 1};
        if (d.hashavshevet) d.hashavshevet = {...d.hashavshevet, send: 1};
    });
};
